"use client";

import { usePathname, useSearchParams } from "next/navigation";
import Link from "next/link";
import { type ReactNode, useState, useEffect, useRef, Suspense } from "react";
import { Home, BarChart3, Sprout, User, Users, Building2, FlaskConical, MapPin, Leaf, UserCog, HandCoins, FileDown, LayoutDashboard, LogOut, LogIn, Menu, X, Truck, Package, Globe, Shield, MessageCircle, Store, HelpCircle, ScanQrCode, Warehouse, Route, LucideIcon, ClipboardCheck, DollarSign, Receipt, FileText, Factory, Activity, Send, AlertTriangle, Droplets, ShieldCheck, ScanLine, ClipboardList, Upload, BookOpen, Landmark, Calculator, Scale, CreditCard, PiggyBank, TrendingDown, Calendar, Eye, Snowflake, Target, Fuel, Navigation, Wrench, FileSignature, Clock, TrendingUp, Map, PanelLeftClose, PanelLeftOpen, ChevronDown, ChevronRight, Sparkles, Bell, Smartphone, Settings, Wallet } from "lucide-react";
import NotificationBell from "./NotificationBell";
import OfflineDot from "./offline/OfflineDot";
import SyncProgress from "./offline/SyncProgress";
// CaonaboWidgetMaybe se monta en app/layout.tsx (fuera de AppShell) para
// que `position:fixed` funcione sin que los transforms del shell creen
// un containing block que mueva el widget.
import CaonaboAlertBellMaybe from "./caonabo/CaonaboAlertBellMaybe";
import { useSession, signOut } from "next-auth/react";
import { useRole } from "@/lib/useRole";
import { usePermisos } from "@/lib/usePermisos";
import { useImpersonate } from "@/lib/useImpersonate";
import { useRouter } from "next/navigation";
import { useSidebarCollapsed } from "@/lib/useSidebarCollapsed";

type NavItem = {
  label: string;
  href: string;
  icon: LucideIcon;
  adminOnly?: boolean;
};

type NavGroup = {
  label: string;
  items: NavItem[];
};

const NAV_GROUPS: NavGroup[] = [
  {
    label: "",
    items: [
      { label: "Inicio", href: "/", icon: Home },
      // App de campo (SPA offline): productores/siembras/aportes/programación
      // que funcionan sin señal. Trae su propio chrome (sale del AppShell).
      { label: "Campo", href: "/campo", icon: Smartphone },
    ],
  },
  {
    label: "Agro",
    items: [
      { label: "Productores", href: "/productores", icon: Users },
      { label: "Fincas y Lotes", href: "/fincas-lotes", icon: MapPin },
      { label: "Siembras", href: "/siembras", icon: Sprout },
      { label: "Acuerdos", href: "/acuerdos", icon: HandCoins },
      { label: "Precios Mercado", href: "/precios-mercado", icon: TrendingUp },
      { label: "Programación", href: "/programacion", icon: BarChart3 },
      { label: "Mapa", href: "/mapa", icon: Globe },
      { label: "Órdenes", href: "/ordenes", icon: Truck },
      // "Viajes" vive en el grupo Logística — removido de Agro para evitar
      // duplicado en el sidebar.
    ],
  },
  {
    // SIDEBAR SIMPLIFICADO — 5 ítems del plan de simplificación de Logística.
    // Cada item consolida los conceptos sueltos del módulo bajo un punto
    // de entrada curado. Las rutas legacy (anticipos, dietas, mapa, pod,
    // tracking, fletes, etc.) NO desaparecen — siguen vivas y accesibles
    // por URL directa o desde los hubs (que las enlazan). El sidebar
    // solo expone los 5 conceptos. Esto cumple el goal explícito del
    // usuario: "5 items, no 24".
    label: "Logística",
    items: [
      { label: "Viajes", href: "/logistica/viajes", icon: Truck },
      { label: "Seguimiento", href: "/logistica/seguimiento", icon: Navigation },
      { label: "Flota", href: "/logistica/flota", icon: Wrench },
      { label: "Operaciones del día", href: "/logistica/dia", icon: Activity },
      // Sprint 1 — Plan de Viaje: provisión de requerimientos cross-área
      { label: "Provisión", href: "/logistica/provision", icon: ClipboardCheck },
      { label: "Liquidaciones", href: "/logistica/liquidaciones", icon: Wallet },
      // Reportes de costo movidos a Portal Contabilidad → Reportes
      // financieros → "Costos logísticos (3 lentes)" para consolidar
      // todos los reportes financieros bajo el portal contable.
      { label: "Telemetría — Cortes", href: "/logistica/telemetria-cortes", icon: BarChart3 },
      // Configuración al final del grupo
      { label: "Configuración", href: "/logistica/config", icon: Settings },
    ],
  },
  {
    label: "Operaciones",
    items: [
      { label: "Recepción", href: "/recepcion", icon: ClipboardCheck },
      // Sprint 6 — tablero de llegadas para recepción
      { label: "Llegadas a planta", href: "/recepcion/llegadas", icon: Truck },
      { label: "Autorizaciones", href: "/operaciones/autorizaciones", icon: AlertTriangle },
      { label: "Inventario", href: "/inventario", icon: Warehouse },
      { label: "Despacho", href: "/despacho", icon: Send },
      { label: "Trazabilidad", href: "/trazabilidad/cadena", icon: Route },
    ],
  },
  {
    label: "Industrial",
    items: [
      { label: "Plan Producción", href: "/industrial/plan", icon: ClipboardList },
      { label: "Entrada Proceso", href: "/industrial/entrada", icon: Package },
      { label: "Lotes", href: "/recepcion/lotes", icon: Package },
      { label: "Lavado/Curado", href: "/industrial/lavado", icon: Droplets },
      { label: "Empaque", href: "/industrial/empaque", icon: Factory },
      { label: "Sorteo VF", href: "/industrial/vf", icon: ScanLine },
      { label: "Control Calidad", href: "/industrial/cc", icon: ShieldCheck },
      { label: "Dashboard CC", href: "/industrial/cc-dashboard", icon: BarChart3 },
      { label: "Despacho", href: "/industrial/despacho", icon: Truck },
      { label: "BRAM", href: "/industrial/bram", icon: Target },
      { label: "Cuarto Frío", href: "/industrial/cuarto-frio", icon: Snowflake },
    ],
  },
  {
    // Sprint 4 — Salida formalizada: captura interna de PedidoCliente.
    label: "Ventas",
    items: [
      { label: "Pedidos de clientes", href: "/ventas/pedidos", icon: ClipboardList },
    ],
  },
  {
    label: "Post-Despacho",
    items: [
      { label: "Entrega CES", href: "/entrega", icon: Truck },
      { label: "Supervisión", href: "/supervision", icon: Eye },
      { label: "Dashboard Tiendas", href: "/supervision/dashboard", icon: Store },
    ],
  },
  {
    label: "Seguimiento",
    items: [
      { label: "Programas", href: "/programas", icon: FlaskConical },
      { label: "Dashboard", href: "/programas/dashboard", icon: LayoutDashboard },
      { label: "Reportes", href: "/reportes", icon: FileDown },
      { label: "Portal Bravo", href: "/bravo", icon: Store },
    ],
  },
  // ⚠ Grupos "POS", "Contabilidad" y "Nómina" removidos del sidebar
  // 2026-05-16 — toda esa funcionalidad vive en el Portal Contabilidad
  // (/portal-contabilidad/*). Las rutas viejas (/pos/*, /contabilidad/*,
  // /finanzas/*, /admin/ecf/*, /nomina/*) SIGUEN VIVAS en `app/` y se
  // pueden visitar por URL directa. Solo desaparecen del menú lateral
  // para no duplicar.

  // Items top-level finales (grupo de label vacío → sin header colapsable):
  {
    label: "",
    items: [
      { label: "Portal Contabilidad", href: "/portal-contabilidad", icon: Calculator },
      { label: "Administración",     href: "/admin/administracion", icon: Settings, adminOnly: true },
    ],
  },
];

// Flat list for top-bar title lookup
const ALL_NAV_ITEMS = NAV_GROUPS.flatMap((g) => g.items);

// Sub-route title overrides (checked before ALL_NAV_ITEMS, most specific first)
const TITLE_OVERRIDES: { prefix: string; label: string }[] = [
  { prefix: "/recepcion/lotes", label: "Lotes" },
];

const BOTTOM_NAV: (NavItem & { isMenu?: boolean })[] = [
  { label: "Inicio", href: "/", icon: Home },
  { label: "Órdenes", href: "/ordenes", icon: Truck },
  { label: "Recepción", href: "/recepcion", icon: ClipboardCheck },
  { label: "Productores", href: "/productores", icon: Users },
  { label: "Menú", href: "#", icon: Menu, isMenu: true },
];

function isActive(pathname: string, href: string) {
  if (href === "/") return pathname === "/";
  return pathname.startsWith(href);
}

/**
 * Activa un item del sidebar si la URL actual matchea su href, O si la
 * URL trae un query param `?from=<href>` (el operador navegó desde un
 * hub y quiere ver ese hub destacado mientras está en una sub-página).
 *
 * Patrón general: cualquier ruta puede pasar `?from=/x` y el item con
 * href=/x se resaltará — útil para todos los hubs del módulo Logística.
 */
function isActiveWithFrom(pathname: string, href: string, fromOverride: string | null) {
  if (fromOverride && fromOverride === href) return true;
  return isActive(pathname, href);
}

const BARE_ROUTES = ["/login", "/login/pin", "/privacidad", "/eliminar-datos", "/pos", "/primer-login"];
const BARE_PREFIXES = ["/chofer", "/firma-acuerdo"];

/**
 * Flyout del sidebar colapsado: cuando el mouse pasa por el icono de un
 * grupo, muestra los items en un panel popup. Reemplaza la versión anterior
 * con CSS-only hover + position:fixed + margins offsets, que cortaba los
 * items cuando el grupo estaba cerca del fondo del viewport.
 *
 * Comportamiento:
 *  - Mouse enter sobre el wrapper → calcula posición y abre.
 *  - Mouse leave del wrapper → cierra después de 120ms (grace period para
 *    que el cursor cruce el gap al flyout sin que se colapse).
 *  - Mouse enter del flyout cancela el cierre programado.
 *  - Flip vertical automático: si no hay espacio debajo del icono para
 *    todos los items, se ancla al BOTTOM del icono y crece hacia arriba.
 *  - maxHeight con scroll interno como red de seguridad para grupos muy
 *    largos.
 */
function CollapsedGroupFlyout({
  group,
  hasActiveItem,
  pathname,
}: {
  group: { label: string; items: NavItem[] };
  hasActiveItem: boolean;
  pathname: string;
}) {
  const GroupIcon = group.items[0]?.icon || Home;
  const triggerRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    maxHeight: number;
  }>({ top: 0, left: 64, maxHeight: 600 });

  function calculatePosition() {
    const trigger = triggerRef.current;
    if (!trigger || typeof window === "undefined") return;
    const rect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const margin = 16;
    const gap = 8;
    // Altura estimada del flyout (header + items + padding interno).
    const estimated = 36 + group.items.length * 36 + 16;
    const spaceBelow = viewportHeight - rect.top - margin;
    const spaceAbove = rect.bottom - margin;
    const leftAnchor = rect.right + gap;

    // Si entra debajo del icono, anclar al top del icono (despliega hacia
    // abajo). Si no, anclar al bottom del icono (despliega hacia arriba).
    if (estimated <= spaceBelow || spaceBelow >= spaceAbove) {
      setPosition({
        top: rect.top,
        bottom: undefined,
        left: leftAnchor,
        maxHeight: Math.max(200, spaceBelow),
      });
    } else {
      setPosition({
        top: undefined,
        bottom: viewportHeight - rect.bottom,
        left: leftAnchor,
        maxHeight: Math.max(200, spaceAbove),
      });
    }
  }

  function handleEnter() {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    calculatePosition();
    setIsOpen(true);
  }

  function handleLeave() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setIsOpen(false), 120);
  }

  // Cleanup timer on unmount.
  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  return (
    <div onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <div
        ref={triggerRef}
        className={`flex items-center justify-center w-10 h-10 mx-auto rounded-xl cursor-pointer transition-all ${
          hasActiveItem
            ? "bg-accent/15 text-accent"
            : "text-text-muted/60 hover:bg-surface-hover hover:text-text"
        }`}
      >
        <GroupIcon size={20} strokeWidth={hasActiveItem ? 2.5 : 1.5} />
      </div>

      {isOpen && (
        <div
          className="fixed z-[9999] transition-opacity duration-150"
          style={{
            top: position.top,
            bottom: position.bottom,
            left: position.left,
            maxHeight: position.maxHeight,
          }}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          <div
            className="rounded-xl shadow-2xl py-2 px-1 min-w-[210px] overflow-y-auto overscroll-contain border border-border"
            style={{
              maxHeight: position.maxHeight,
              background: "rgba(13, 13, 26, 0.97)",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
            }}
          >
            <p
              className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-muted/60 sticky top-0 z-10"
              style={{ background: "rgba(13, 13, 26, 0.97)" }}
            >
              {group.label}
            </p>
            {group.items.map((item) => {
              const Icon = item.icon;
              const active = isActive(pathname, item.href);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-colors min-h-[36px] ${
                    active
                      ? "bg-accent/10 text-accent font-semibold"
                      : "text-text-muted hover:bg-white/5 hover:text-text"
                  }`}
                >
                  <Icon size={16} strokeWidth={active ? 2.5 : 1.5} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Lee `?from=<href>` del URL y lo reporta al padre via setter. Se monta
 * dentro de un <Suspense> boundary para evitar que rompa el pre-render
 * estático de todas las páginas (Next 14 exige que `useSearchParams` esté
 * envuelto en Suspense durante el SSG, ver
 * https://nextjs.org/docs/messages/missing-suspense-with-csr-bailout).
 */
function FromOverrideReader({
  onChange,
}: {
  onChange: (from: string | null) => void;
}) {
  const searchParams = useSearchParams();
  const from = searchParams?.get("from") ?? null;
  useEffect(() => {
    onChange(from);
  }, [from, onChange]);
  return null;
}

export function AppShell({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  // `fromOverride` se lee desde `?from=<href>` para mantener el item
  // padre del sidebar resaltado mientras el operador está en una
  // sub-página. La lectura ocurre dentro de <Suspense> abajo.
  const [fromOverride, setFromOverride] = useState<string | null>(null);
  const router = useRouter();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [kbOpen, setKbOpen] = useState(false);
  const { role, isAdmin, canAccess, loading: roleLoading, esChofer } = useRole();
  // esChofer viene del useRole — calculado dinámicamente desde permisos
  // chofer.* / firma.* (no hardcode de nombre del perfil). Single source
  // of truth en lib/auth-chofer.ts.
  const tienePerfilChofer = esChofer;
  const { collapsed, toggle: toggleSidebar } = useSidebarCollapsed();

  // Hydration guard: durante el primer render del cliente, useRole() puede
  // devolver isAdmin=true mientras el server renderizó isAdmin=false, causando
  // mismatch en items adminOnly. Esperamos al primer effect (mount) para
  // recién entonces aplicar los flags reales — así el initial-render cliente
  // coincide con el HTML del server.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const isAdminEffective = mounted ? isAdmin : false;
  const canAccessEffective = mounted ? canAccess : () => true;

  // Track which nav groups are expanded (desktop sidebar)
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  // Track which nav groups are expanded (mobile drawer) — only active section open by default
  const [mobileOpenGroups, setMobileOpenGroups] = useState<Set<string>>(new Set());

  // Auto-expand the group containing the active route
  useEffect(() => {
    for (const g of NAV_GROUPS) {
      if (g.label && g.items.some((item) => isActiveWithFrom(pathname, item.href, fromOverride))) {
        setOpenGroups((prev) => {
          if (prev.has(g.label)) return prev;
          const next = new Set(prev);
          next.add(g.label);
          return next;
        });
        // Mobile: only keep active group open
        setMobileOpenGroups(new Set([g.label]));
        break;
      }
    }
  }, [pathname, fromOverride]);

  function toggleGroup(label: string) {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }

  function toggleMobileGroup(label: string) {
    setMobileOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }

  // Impersonate support: admin viendo como otro usuario.
  // Hook compartido (lib/useImpersonate.ts) — la misma data se consume en
  // UserMenu, UserAvatar y banner para consistencia.
  const impersonate = useImpersonate();
  const isImpersonating = impersonate !== null;

  const isBare = BARE_ROUTES.includes(pathname) || BARE_PREFIXES.some((p) => pathname.startsWith(p));

  // Auto-redirect chofer users to /chofer portal (skip if admin is impersonating)
  useEffect(() => {
    if (roleLoading) return;
    if (isImpersonating) return;
    if (role === "chofer" && !pathname.startsWith("/chofer") && !pathname.startsWith("/jornadas") && !BARE_ROUTES.includes(pathname)) {
      router.replace("/chofer");
    }
  }, [role, roleLoading, pathname, router, isImpersonating]);

  // Redirect to primer-login if first login
  useEffect(() => {
    if (roleLoading) return;
    if (pathname.startsWith("/primer-login") || pathname === "/login") return;
    fetch("/api/auth/check-primer-login")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.primerLogin && !pathname.startsWith("/primer-login")) {
          router.replace("/primer-login");
        }
      })
      .catch(() => {});
  }, [roleLoading, pathname, router]);

  useEffect(() => {
    if (isBare) return;
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen, isBare]);

  // Hide bottom nav when virtual keyboard is open
  useEffect(() => {
    // El ocultar/mostrar la barra inferior se hace de forma declarativa con el
    // estado `kbOpen` (clase translate-y-full) + `md:hidden`. NO tocar
    // `style.display` imperativamente: un display inline anula el `md:hidden` y
    // hacía que la barra apareciera en escritorio junto al sidebar (doble nav).
    let nativeHandled = false;
    const onCapShow = () => { nativeHandled = true; setKbOpen(true); };
    const onCapHide = () => { setKbOpen(false); };
    window.addEventListener("cap-keyboard-show", onCapShow);
    window.addEventListener("cap-keyboard-hide", onCapHide);

    // Fallback: visualViewport for web/PWA
    if (typeof window !== "undefined" && window.visualViewport) {
      const vv = window.visualViewport;
      const onResize = () => {
        if (nativeHandled) return;
        const isKeyboard = vv.height < window.innerHeight * 0.75;
        setKbOpen(isKeyboard);
      };
      vv.addEventListener("resize", onResize);
      return () => {
        window.removeEventListener("cap-keyboard-show", onCapShow);
        window.removeEventListener("cap-keyboard-hide", onCapHide);
        vv.removeEventListener("resize", onResize);
      };
    }

    return () => {
      window.removeEventListener("cap-keyboard-show", onCapShow);
      window.removeEventListener("cap-keyboard-hide", onCapHide);
    };
  }, []);

  // Render without shell on auth/public/chofer pages (after all hooks)
  if (isBare) {
    return <>{children}</>;
  }

  const visibleGroups = (() => {
    const base = NAV_GROUPS
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (item) => (!item.adminOnly || isAdminEffective) && canAccessEffective(item.href),
        ),
      }))
      .filter((g) => g.items.length > 0);

    // Inyectar UN solo item "Chofer" → /chofer si el user tiene permisos
    // chofer.* (detectado dinámicamente via esChofer del hook).
    // Se ubica JUSTO ARRIBA de "Portal Contabilidad" en el grupo final
    // de items top-level (label="" al final del sidebar).
    if (tienePerfilChofer) {
      const choferItem: NavItem = { label: "Chofer", href: "/chofer", icon: Truck };
      // Buscar el grupo que contiene "Portal Contabilidad"
      const idxGrupoFinal = base.findIndex((g) =>
        g.items.some((it) => it.href === "/portal-contabilidad"),
      );
      if (idxGrupoFinal >= 0) {
        const grupo = base[idxGrupoFinal];
        const idxPortalCont = grupo.items.findIndex((it) => it.href === "/portal-contabilidad");
        const nuevosItems = [...grupo.items];
        nuevosItems.splice(idxPortalCont, 0, choferItem); // antes de Portal Contabilidad
        base[idxGrupoFinal] = { ...grupo, items: nuevosItems };
      } else {
        // Fallback: agregar como grupo nuevo al final
        base.push({ label: "", items: [choferItem] });
      }
    }
    return base;
  })();

  const titleOverride = TITLE_OVERRIDES.find((t) => pathname.startsWith(t.prefix));
  const current = ALL_NAV_ITEMS.find((item) => isActive(pathname, item.href));
  const currentLabel = titleOverride?.label ?? current?.label ?? "Cacique Agro";

  return (
    <div className="page-container">
      {/* useSearchParams aislado en su propio Suspense — exigencia de
          Next 14 SSG. No bloquea el árbol; setFromOverride solo se llama
          después de hidratar el cliente. */}
      <Suspense fallback={null}>
        <FromOverrideReader onChange={setFromOverride} />
      </Suspense>
      {/* Banner de impersonación — mobile-friendly. Muestra nombre + rol(es)
          del usuario impersonado para que el admin sepa exactamente qué
          contexto está viendo. Incluye spinner mientras `roleLoading` para
          señalar que los permisos del impersonado se están cargando. */}
      {isImpersonating && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-red-600 px-3 py-2 text-white shadow-lg safe-top">
          <div className="flex items-center justify-between gap-2 max-w-screen-xl mx-auto">
            <div className="flex flex-col min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {roleLoading && (
                  <span
                    className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0"
                    aria-label="Cargando permisos del impersonado"
                  />
                )}
                <span className="text-xs sm:text-sm font-medium truncate">
                  {roleLoading ? "Cargando vista de: " : "Viendo como: "}
                  <strong>{impersonate!.nombre}</strong>
                </span>
              </div>
              {(() => {
                const rolesList = impersonate!.roles ?? impersonate!.perfiles ?? [];
                const rolesText = rolesList.length > 0 ? rolesList.join(", ") : "sin roles";
                const legacyRole = impersonate!.role ?? "—";
                return (
                  <span className="text-[10px] sm:text-xs text-white/80 truncate">
                    Rol legacy: <strong>{legacyRole}</strong> · Roles: <strong>{rolesText}</strong>
                  </span>
                );
              })()}
            </div>
            <button
              onClick={() => {
                sessionStorage.removeItem("cacique_impersonate");
                window.location.reload();
              }}
              className="flex-shrink-0 rounded-lg bg-white/20 px-4 py-2 text-xs font-bold transition hover:bg-white/30 active:scale-95 min-h-[36px] min-w-[60px]"
            >
              Salir
            </button>
          </div>
        </div>
      )}

      {/* Sidebar desktop — collapsible */}
      <aside
        className={`sidebar transition-all duration-200 ${collapsed ? "sidebar-collapsed" : ""}`}
        style={{ width: collapsed ? 64 : undefined }}
      >
        <Link href="/" className="sidebar-logo hover:opacity-80 transition-opacity">
          <div className="sidebar-logo-mark">
            <img
              src="/images/isotipo.png"
              alt="Cacique"
              className="w-9 h-9 rounded-[10px] object-contain"
            />
          </div>
          {!collapsed && (
            <div>
              <div className="sidebar-logo-sub">CACIQUE</div>
              <div className="sidebar-logo-title">Agro ERP</div>
            </div>
          )}
        </Link>
        <nav className="sidebar-nav">
          {visibleGroups.map((group, idx) => {
            // Dos grupos en NAV_GROUPS tienen label "" (Inicio + Portal/Admin).
            // Sin idx en la key, React tira "two children with the same key
            // `_home`" y rinde uno solo. Mantener el grupo por discriminador
            // basado en el primer href además del idx para estabilidad si
            // cambian de orden.
            const groupKey = group.label || `_home_${group.items[0]?.href ?? idx}`;
            const isGroupOpen = !group.label || openGroups.has(group.label);
            const hasActiveItem = group.items.some((item) => isActiveWithFrom(pathname, item.href, fromOverride));
            const GroupIcon = group.items[0]?.icon || Home;

            return (
              <div key={groupKey}>
                {/* ── Expanded sidebar ── */}
                {!collapsed && (
                  <>
                    {group.label && (
                      <button
                        onClick={() => toggleGroup(group.label)}
                        className="w-full flex items-center justify-between px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted/60 hover:text-text-muted transition"
                      >
                        <span>{group.label}</span>
                        {isGroupOpen ? (
                          <ChevronDown size={12} className="text-text-muted/40" />
                        ) : (
                          <ChevronRight size={12} className="text-text-muted/40" />
                        )}
                      </button>
                    )}
                    {(isGroupOpen || !group.label) &&
                      group.items.map((item) => {
                        const Icon = item.icon;
                        const active = isActiveWithFrom(pathname, item.href, fromOverride);
                        return (
                          <Link key={item.href} href={item.href}
                            className={`sidebar-item flex items-center gap-2 ${active ? "active" : ""}`}>
                            <Icon size={18} strokeWidth={active ? 2.5 : 1.5} />
                            <span>{item.label}</span>
                          </Link>
                        );
                      })}
                  </>
                )}

                {/* ── Collapsed sidebar — group icon with hover flyout ── */}
                {collapsed && (
                  <div className="px-1 py-0.5">
                    {group.label ? (
                      <CollapsedGroupFlyout
                        group={group}
                        hasActiveItem={hasActiveItem}
                        pathname={pathname}
                      />
                    ) : (
                      group.items.map((item) => {
                        const Icon = item.icon;
                        const active = isActiveWithFrom(pathname, item.href, fromOverride);
                        return (
                          <Link key={item.href} href={item.href} title={item.label}
                            className={`sidebar-item flex items-center justify-center px-0 ${active ? "active" : ""}`}>
                            <Icon size={18} strokeWidth={active ? 2.5 : 1.5} />
                          </Link>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="border-t border-border-subtle">
          {/* Collapse toggle button */}
          <button
            onClick={toggleSidebar}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-xs text-text-muted hover:text-text hover:bg-surface-hover transition"
            title={collapsed ? "Expandir menú" : "Colapsar menú"}
          >
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            {!collapsed && <span>Colapsar</span>}
          </button>
          {!collapsed && (
            <div className="px-4 pb-4 pt-1 space-y-2">
              <UserMenu />
              <HelpButton />
            </div>
          )}
        </div>
      </aside>

      {/* Main area */}
      <div className={`main-content ${collapsed ? "main-content-collapsed" : ""}`}>
        {/* Top bar — solo branding, no link */}
        <header className="top-bar">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-1.5 rounded-lg transition hover:bg-surface-hover active:scale-90"
              aria-label="Abrir menú"
            >
              <Menu size={22} strokeWidth={1.5} className="text-text" />
            </button>
            <Link href="/" className="md:hidden active:scale-90 transition-transform duration-200 hover:opacity-80">
              <img
                src="/images/isotipo.png"
                alt="Cacique"
                className="w-8 h-8 rounded-lg object-contain"
              />
            </Link>
            <h1 className="top-bar-title">{currentLabel}</h1>
          </div>
          <div className="flex items-center gap-2">
            <SyncProgress />
            <OfflineDot />
            <CaonaboAlertBellMaybe />
            <NotificationBell />
            <UserAvatar />
          </div>
        </header>

        {/* Page content */}
        <main className="page-main">
          <div className="content-area">{children}</div>
        </main>
      </div>

      {/* Mobile drawer overlay — onTouchEnd to avoid iOS ghost taps */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
          onTouchEnd={(e) => { e.preventDefault(); setMobileMenuOpen(false); }}
        />
      )}

      {/* Mobile slide-out drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-[70] w-72 max-w-[80vw] flex flex-col border-r border-border transition-transform duration-300 ease-out md:hidden ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{
          backgroundColor: "rgba(13, 13, 26, 0.98)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          willChange: "transform",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-4 pt-5 pb-4 border-b border-border-subtle safe-top">
          <Link
            href="/"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <img
              src="/images/isotipo.png"
              alt="Cacique"
              className="w-8 h-8 rounded-lg object-contain"
            />
            <div>
              <div className="text-[10px] font-semibold tracking-wider text-text-muted uppercase">Cacique</div>
              <div className="text-sm font-bold text-text leading-none">Agro ERP</div>
            </div>
          </Link>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-1.5 rounded-lg transition hover:bg-surface-hover active:scale-90"
            aria-label="Cerrar menú"
          >
            <X size={20} className="text-text-muted" />
          </button>
        </div>

        {/* Drawer nav items — collapsible groups */}
        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5">
          {visibleGroups.map((group, idx) => {
            // Misma razón que en desktop: dos grupos comparten label "".
            const groupKey = group.label || `_home_${group.items[0]?.href ?? idx}`;
            const hasActiveItem = group.items.some((item) => isActiveWithFrom(pathname, item.href, fromOverride));
            const isMobileOpen = !group.label || mobileOpenGroups.has(group.label);

            // Groups without label (Inicio) — render items directly
            if (!group.label) {
              return group.items.map((item) => {
                const Icon = item.icon;
                const active = isActiveWithFrom(pathname, item.href, fromOverride);
                return (
                  <Link key={item.href} href={item.href} onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all min-h-[44px] ${
                      active ? "bg-accent/10 text-accent font-semibold" : "text-text-muted hover:bg-surface-hover hover:text-text"
                    }`}>
                    <Icon size={18} strokeWidth={active ? 2.5 : 1.5} />
                    <span>{item.label}</span>
                  </Link>
                );
              });
            }

            // Collapsible group
            const firstIcon = group.items[0]?.icon;
            const GroupIcon = firstIcon;
            return (
              <div key={groupKey}>
                <button
                  onClick={() => toggleMobileGroup(group.label)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all min-h-[44px] ${
                    hasActiveItem && !isMobileOpen
                      ? "text-accent"
                      : "text-text-muted hover:bg-surface-hover hover:text-text"
                  }`}
                >
                  <GroupIcon size={16} strokeWidth={1.5} className={hasActiveItem ? "text-accent" : "text-text-muted/60"} />
                  <span className="flex-1 text-left font-medium text-xs uppercase tracking-wider">{group.label}</span>
                  {hasActiveItem && !isMobileOpen && (
                    <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                  )}
                  <ChevronDown
                    size={14}
                    className={`text-text-muted/40 transition-transform duration-200 ${isMobileOpen ? "rotate-0" : "-rotate-90"}`}
                  />
                </button>

                <div
                  className="grid transition-all duration-200 ease-out"
                  style={{ gridTemplateRows: isMobileOpen ? "1fr" : "0fr" }}
                >
                  <div className="overflow-hidden">
                    <div className="pl-3 space-y-0.5 pb-1">
                      {group.items.map((item) => {
                        const Icon = item.icon;
                        const active = isActiveWithFrom(pathname, item.href, fromOverride);
                        return (
                          <Link key={item.href} href={item.href} onClick={() => setMobileMenuOpen(false)}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all min-h-[42px] ${
                              active ? "bg-accent/10 text-accent font-semibold" : "text-text-muted hover:bg-surface-hover hover:text-text"
                            }`}>
                            <Icon size={16} strokeWidth={active ? 2.5 : 1.5} />
                            <span>{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        {/* Drawer footer — user info */}
        <div className="px-4 pb-4 pt-2 border-t border-border-subtle space-y-2">
          <UserMenu onNavigate={() => setMobileMenuOpen(false)} />
          <HelpButton />
        </div>
      </aside>

      {/* Bottom nav mobile — quick access */}
      <nav
        id="bottom-nav"
        className={`fixed bottom-0 left-0 right-0 z-50 border-t border-border safe-bottom md:hidden transition-all duration-200 ${kbOpen ? "translate-y-full opacity-0 pointer-events-none" : ""}`}
        style={{
          backgroundColor: "rgba(13, 13, 26, 0.92)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
        }}
      >
        <div className="flex w-full items-center justify-around px-2 py-1.5">
          {BOTTOM_NAV.filter((item) => item.isMenu || canAccessEffective(item.href)).map((item) => {
            const Icon = item.icon;
            const active = !item.isMenu && isActive(pathname, item.href);

            if (item.isMenu) {
              return (
                <button
                  key="menu"
                  type="button"
                  onClick={() => setMobileMenuOpen(true)}
                  className="group flex flex-col items-center gap-0.5 py-1.5 px-3 rounded-xl transition-all duration-200 ease-out active:scale-[0.85]"
                >
                  <div className="relative p-1.5 rounded-xl transition-all duration-300 ease-out group-hover:bg-surface-hover group-hover:scale-105 group-active:bg-surface-hover">
                    <Icon
                      size={22}
                      strokeWidth={1.5}
                      className="transition-all duration-300 text-[#5A6B80] group-hover:text-text"
                    />
                  </div>
                  <span className="text-[11px] text-[#5A6B80] font-normal group-hover:text-text transition-all duration-300">
                    {item.label}
                  </span>
                </button>
              );
            }

            return (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col items-center gap-0.5 py-1.5 px-3 rounded-xl transition-all duration-200 ease-out active:scale-[0.85]"
              >
                <div
                  className={`relative p-1.5 rounded-xl transition-all duration-300 ease-out ${
                    active
                      ? "bg-accent/15 scale-110"
                      : "group-hover:bg-surface-hover group-hover:scale-105 group-active:bg-surface-hover"
                  }`}
                >
                  <Icon
                    size={22}
                    strokeWidth={active ? 2.5 : 1.5}
                    className={`transition-all duration-300 ${
                      active ? "text-accent" : "text-[#5A6B80] group-hover:text-text"
                    }`}
                  />
                  {active && (
                    <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-accent" />
                  )}
                </div>
                <span
                  className={`text-[11px] transition-all duration-300 ${
                    active
                      ? "text-accent font-semibold translate-y-0"
                      : "text-[#5A6B80] font-normal group-hover:text-text"
                  }`}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

/* ── Sidebar user menu ─────────────────────────────── */
function UserMenu({ onNavigate }: { onNavigate?: () => void }) {
  const { data: session, status } = useSession();
  const impersonate = useImpersonate();

  if (status === "loading") {
    return (
      <div className="flex items-center gap-2 animate-pulse">
        <div className="w-8 h-8 rounded-full bg-surface-hover" />
        <div className="h-3 w-20 rounded bg-surface-hover" />
      </div>
    );
  }

  if (!session?.user) {
    return (
      <Link
        href="/login"
        onClick={onNavigate}
        className="flex items-center gap-2 rounded-xl px-2 py-2 text-sm text-text-muted transition hover:bg-surface-hover hover:text-text"
      >
        <LogIn size={16} />
        <span>Iniciar sesión</span>
      </Link>
    );
  }

  // Durante "Ver como", mostrar la info del usuario impersonado en lugar del admin.
  // El admin sigue siendo quien controla la sesión real (signOut, etc.) pero la
  // identidad visible refleja el contexto que está probando.
  const isImpersonating = impersonate !== null;
  const displayName = isImpersonating ? impersonate!.nombre : session.user.name;
  const displayEmail = isImpersonating
    ? `Impersonando · admin real: ${session.user.email}`
    : session.user.email;
  const displayImage = isImpersonating ? null : session.user.image;
  const initial = (displayName ?? "U")[0].toUpperCase();

  return (
    <div className="space-y-2">
      <div
        className={`flex items-center gap-2 rounded-xl p-2 ${
          isImpersonating ? "bg-red-500/10 ring-1 ring-red-500/30" : ""
        }`}
      >
        {displayImage ? (
          <img
            src={displayImage}
            alt=""
            className="w-8 h-8 rounded-full object-cover ring-1 ring-border"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
              isImpersonating
                ? "bg-red-500/30 text-red-200"
                : "bg-accent/20 text-accent"
            }`}
          >
            {initial}
          </div>
        )}
        <div className="min-w-0">
          <p className="text-xs font-medium text-text truncate flex items-center gap-1">
            {isImpersonating && (
              <span className="text-[9px] px-1 py-0.5 rounded bg-red-500/30 text-red-200 font-bold">
                IMPERSONANDO
              </span>
            )}
            {displayName}
          </p>
          <p className="text-[10px] text-text-muted truncate">{displayEmail}</p>
        </div>
      </div>
      {isImpersonating && (
        <button
          onClick={() => {
            sessionStorage.removeItem("cacique_impersonate");
            window.location.reload();
          }}
          className="flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-xs text-red-400 transition hover:bg-red-500/10 font-medium"
        >
          <X size={14} />
          <span>Salir de "Ver como"</span>
        </button>
      )}
      <button
        onClick={() => signOut({ callbackUrl: "/login" })}
        className="flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-xs text-text-muted transition hover:bg-red-500/10 hover:text-red-400"
      >
        <LogOut size={14} />
        <span>{isImpersonating ? "Cerrar sesión admin" : "Cerrar sesión"}</span>
      </button>
    </div>
  );
}

/* ── Top-bar user avatar ──────────────────────────── */
function UserAvatar() {
  const { data: session, status } = useSession();
  const impersonate = useImpersonate();
  const [menuOpen, setMenuOpen] = useState(false);

  if (status === "loading") return null;

  if (!session?.user) {
    return (
      <Link
        href="/login"
        className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-text-muted transition hover:bg-surface-hover hover:text-text"
      >
        <LogIn size={14} />
        <span className="hidden sm:inline">Iniciar sesión</span>
      </Link>
    );
  }

  // Durante impersonación, mostrar info del usuario impersonado.
  const isImpersonating = impersonate !== null;
  const displayName = isImpersonating ? impersonate!.nombre : (session.user.name ?? "U");
  const displayEmail = session.user.email; // admin's email always (es quien está logueado)
  const displayImage = isImpersonating ? null : session.user.image;
  const initial = displayName[0].toUpperCase();

  return (
    <div className="relative">
      <button
        onClick={() => setMenuOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-lg transition hover:opacity-80 active:scale-95"
      >
        {displayImage ? (
          <img
            src={displayImage}
            alt=""
            className={`w-7 h-7 rounded-full object-cover ring-1 ${
              isImpersonating ? "ring-red-500" : "ring-border"
            }`}
            referrerPolicy="no-referrer"
          />
        ) : (
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold ring-1 ${
              isImpersonating
                ? "bg-red-500/30 text-red-200 ring-red-500"
                : "bg-accent/20 text-accent ring-transparent"
            }`}
          >
            {initial}
          </div>
        )}
      </button>
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-0 top-full mt-2 z-50 w-56 max-w-[calc(100vw-1rem)] rounded-xl border border-border bg-surface p-3 shadow-xl space-y-2">
            <div className={`flex items-center gap-2 rounded-xl p-1 ${isImpersonating ? "bg-red-500/10" : ""}`}>
              {displayImage ? (
                <img
                  src={displayImage}
                  alt=""
                  className="w-8 h-8 rounded-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    isImpersonating ? "bg-red-500/30 text-red-200" : "bg-accent/20 text-accent"
                  }`}
                >
                  {initial}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-xs font-medium text-text truncate flex items-center gap-1">
                  {isImpersonating && (
                    <span className="text-[8px] px-1 rounded bg-red-500/30 text-red-200 font-bold">
                      VER COMO
                    </span>
                  )}
                  {displayName}
                </p>
                <p className="text-[10px] text-text-muted truncate">
                  {isImpersonating ? `admin real: ${displayEmail}` : displayEmail}
                </p>
              </div>
            </div>
            <hr className="border-border" />
            {isImpersonating && (
              <button
                onClick={() => {
                  sessionStorage.removeItem("cacique_impersonate");
                  window.location.reload();
                }}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-red-400 hover:bg-red-500/10 font-medium transition"
              >
                <X size={14} />
                Salir de "Ver como"
              </button>
            )}
            <button
              onClick={() => signOut({ callbackUrl: "/login" })}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-text-muted transition hover:bg-red-500/10 hover:text-red-400"
            >
              <LogOut size={14} />
              {isImpersonating ? "Cerrar sesión admin" : "Cerrar sesión"}
            </button>
          </div>
        </>
      )}
      {/* CaonaboWidget vive en app/layout.tsx (fuera del shell). */}
    </div>
  );
}

/* ── Help / restart tour button ─────────────────────── */
function HelpButton() {
  const { data: session } = useSession();
  if (!session?.user) return null;

  return (
    <button
      onClick={() => window.dispatchEvent(new Event("restart-onboarding"))}
      className="flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-xs text-text-muted transition hover:bg-surface-hover hover:text-text"
      title="Reiniciar tour de ayuda"
    >
      <HelpCircle size={14} />
      <span>Tour de ayuda</span>
    </button>
  );
}

export default AppShell;

